import { useParams, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle, XCircle, CreditCard, ShieldCheck, Clock } from 'lucide-react'
import Layout from '../../components/Layout'
import Spinner from '../../components/Spinner'
import { getAdminStats } from '../../api/users'
import { useTelegram } from '../../hooks/useTelegram'
import { AdminActivity } from '../../types'

const EVENT_LABELS: Record<string, string> = {
  order_status_change: 'Статус заявки изменён',
  payment_received:    'Платёж подтверждён',
  payment_rejected:    'Платёж отклонён',
  user_verified:       'Пользователь верифицирован',
  client_blocked:      'Клиент заблокирован',
  client_updated:      'Клиент обновлён',
  settings_changed:    'Настройки изменены',
  admin_changed:       'Роль изменена',
  discount_applied:    'Скидка применена',
  permissions_changed: 'Права изменены',
}

const EVENT_COLORS: Record<string, string> = {
  order_status_change: '#F97316',
  payment_received:    '#34C759',
  payment_rejected:    '#D97757',
  user_verified:       '#30B0C7',
  client_blocked:      '#D97757',
  default:             '#8A8A96',
}

function formatDt(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function StatCard({ value, label, icon, color }: { value: number; label: string; icon: React.ReactNode; color: string }) {
  return (
    <div className="card flex flex-col items-center gap-1 py-4">
      <div style={{ color }}>{icon}</div>
      <p className="text-2xl font-bold text-tg-text">{value}</p>
      <p className="text-xs text-tg-hint text-center">{label}</p>
    </div>
  )
}

function ActivityItem({ item }: { item: AdminActivity }) {
  const label = EVENT_LABELS[item.event_type] ?? item.event_type
  const color = EVENT_COLORS[item.event_type] ?? EVENT_COLORS.default

  return (
    <div className="flex gap-3 py-2.5 border-b border-tg-hint/10 last:border-0">
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, marginTop: 6, flexShrink: 0 }} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-tg-text">{label}</p>
        <p className="text-xs text-tg-hint truncate">{item.description}</p>
        {item.order_id && (
          <p className="text-xs" style={{ color: '#F97316' }}>Заявка #{item.order_id}</p>
        )}
      </div>
      <span className="text-xs text-tg-hint flex-shrink-0 pt-0.5">{formatDt(item.occurred_at)}</span>
    </div>
  )
}

export default function AdminDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { tg } = useTelegram()

  useEffect(() => {
    tg?.BackButton.show()
    const cb = () => navigate(-1)
    tg?.BackButton.onClick(cb)
    return () => { tg?.BackButton.offClick(cb); tg?.BackButton.hide() }
  }, []) // eslint-disable-line

  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin-stats', id],
    queryFn: () => getAdminStats(Number(id)),
    enabled: !!id,
  })

  if (isLoading) return <Spinner className="h-screen" />
  if (!stats) return null

  const { admin, orders_approved, orders_rejected, payments_confirmed, verifications, activity } = stats
  const total_orders = orders_approved + orders_rejected

  return (
    <Layout hideNav>
      <button
        onClick={() => navigate(-1)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#A8A8AE', marginBottom: 16, fontSize: 13, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        Назад
      </button>

      {/* Admin header */}
      <div className="card mb-4">
        <div className="flex items-center gap-3 mb-3">
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(249,115,22,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: '#F97316' }}>
              {admin.full_name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-semibold text-tg-text">{admin.full_name}</p>
            <p className="text-xs text-tg-hint">{admin.phone}</p>
            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: 'rgba(249,115,22,0.12)', color: '#F97316' }}>
              {admin.role === 'superadmin' ? 'Суперадмин' : 'Администратор'}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-tg-hint">Telegram ID</span>
            <p className="text-tg-text font-mono">{admin.telegram_id}</p>
          </div>
          <div>
            <span className="text-tg-hint">В системе с</span>
            <p className="text-tg-text">{formatDate(admin.created_at)}</p>
          </div>
          <div className="col-span-2">
            <span className="text-tg-hint">Последняя активность</span>
            <p className="text-tg-text">{formatDt(admin.last_seen_at)}</p>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <StatCard
          value={orders_approved}
          label="Заявок одобрено"
          icon={<CheckCircle size={22} />}
          color="#34C759"
        />
        <StatCard
          value={orders_rejected}
          label="Заявок отклонено"
          icon={<XCircle size={22} />}
          color="#D97757"
        />
        <StatCard
          value={payments_confirmed}
          label="Платежей принято"
          icon={<CreditCard size={22} />}
          color="#F97316"
        />
        <StatCard
          value={verifications}
          label="Верификаций"
          icon={<ShieldCheck size={22} />}
          color="#30B0C7"
        />
      </div>

      {/* Efficiency */}
      {total_orders > 0 && (
        <div className="card mb-4">
          <p className="section-title mb-2">Эффективность</p>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full" style={{ background: 'rgba(255,255,255,0.07)' }}>
              <div
                className="h-2 rounded-full"
                style={{ width: `${Math.round((orders_approved / total_orders) * 100)}%`, background: '#34C759' }}
              />
            </div>
            <span className="text-sm font-bold text-tg-text">
              {Math.round((orders_approved / total_orders) * 100)}%
            </span>
          </div>
          <p className="text-xs text-tg-hint mt-1">Одобрено из {total_orders} заявок</p>
        </div>
      )}

      {/* Activity log */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={14} className="text-tg-hint" />
          <p className="section-title">Последняя активность</p>
          {activity.length > 0 && (
            <span className="text-xs text-tg-hint ml-auto">{activity.length} событий</span>
          )}
        </div>
        {activity.length > 0 ? (
          <div>
            {activity.map((item) => (
              <ActivityItem key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-tg-hint py-4 text-center">Активности пока нет</p>
        )}
      </div>
    </Layout>
  )
}
